<script setup lang="ts">
import { dashboardApiClient } from '@api-client/dashboard';
import type { ResType } from '@api-client/typeUtils';
import { currentWebsiteId } from '@partner-spa-vue/stores/MyWebsitesStore';
import { onMounted, ref } from 'vue';
import { formatDate } from '@lib/utils/formatDate';
import { formatCurrency } from '@lib/utils/formatCurrency';
import { toast } from '@/ui-plus/sonner';
import { Loader2, BanknoteArrowDown } from 'lucide-vue-next';
import { Button } from '@/components/ui/button';

type BalancesResponse = ResType<
    typeof dashboardApiClient.website.partner_marketing.store_balances[':websiteId']['$get']
>["data"];
type DeliveryteamBalancesResponse = ResType<
    typeof dashboardApiClient.website.partner_marketing.deliveryteam_balances[':websiteId']['$get']
>["data"];

const balances = ref<BalancesResponse | null>(null);
const deliveryteamBalances = ref<DeliveryteamBalancesResponse | null>(null);
const loading = ref(false);
const transferringCurrency = ref<string | null>(null);
const transferringDeliveryteamCurrency = ref<string | null>(null);

const loadBalances = async (silent = false) => {
    if (!silent) loading.value = true;
    const [storeRes, deliveryteamRes] = await Promise.all([
        (await dashboardApiClient.website.partner_marketing.store_balances[':websiteId'].$get({
            param: { websiteId: currentWebsiteId.value || '' }
        })).json(),
        (await dashboardApiClient.website.partner_marketing.deliveryteam_balances[':websiteId'].$get({
            param: { websiteId: currentWebsiteId.value || '' }
        })).json(),
    ]);

    if (storeRes.error) {
        toast.error(storeRes.error.message || 'Failed to load earnings data');
    } else {
        balances.value = storeRes.data;
    }
    if (deliveryteamRes.error) {
        toast.error(deliveryteamRes.error.message || 'Failed to load deliveryteam earnings data');
    } else {
        deliveryteamBalances.value = deliveryteamRes.data;
    }
    if (!silent) loading.value = false;
};

const requestTransfer = async (currency: string) => {
    if (transferringCurrency.value) return;
    transferringCurrency.value = currency;
    try {
        const res = await (await dashboardApiClient.website.partner_marketing.store_balances[':websiteId'].request_transfer.$post({
            param: { websiteId: currentWebsiteId.value || '' },
            json: { currency },
        })).json();

        if (res.error) {
            toast.error(res.error.message || 'Transfer failed');
        } else {
            toast.success(`Transferred ${formatCurrency(res.data.amount, res.data.currency)}`);
            await loadBalances(true);
        }
    } catch {
        toast.error('Transfer failed');
    } finally {
        transferringCurrency.value = null;
    }
};

const requestDeliveryteamTransfer = async (currency: string) => {
    if (transferringDeliveryteamCurrency.value) return;
    transferringDeliveryteamCurrency.value = currency;
    try {
        const res = await (await dashboardApiClient.website.partner_marketing.deliveryteam_balances[':websiteId'].request_transfer.$post({
            param: { websiteId: currentWebsiteId.value || '' },
            json: { currency },
        })).json();

        if (res.error) {
            toast.error(res.error.message || 'Transfer failed');
        } else {
            toast.success(`Transferred ${formatCurrency(res.data.amount, res.data.currency)}`);
            await loadBalances(true);
        }
    } catch {
        toast.error('Transfer failed');
    } finally {
        transferringDeliveryteamCurrency.value = null;
    }
};

onMounted(() => {
    loadBalances();
});

</script>

<template>
    <div>
        <div v-if="loading && !balances"
             class="flex items-center justify-center py-12">
            <Loader2 class="h-8 w-8 animate-spin" />
            <span class="ml-2">Loading earnings data...</span>
        </div>

        <div v-if="balances && !loading">
            <br>
            <div v-if="balances.length === 0 && (!deliveryteamBalances || deliveryteamBalances.length === 0)"
                 class="text-center py-8 text-muted-foreground">
                Your earnings will appear here once incoming amounts are settled.
                <br>
                After settlement, you can view your balance and request a payout.
            </div>
            <div v-else
                 class="space-y-3">
                <div v-if="balances.length > 0">
                    <h3 class="font-medium mb-2">Stores</h3>
                    <div class="space-y-3">
                        <div v-for="balance in balances"
                             :key="balance.currency"
                             class="flex items-center justify-between p-3 border rounded-lg gap-3">
                            <div class="min-w-0">
                                <p class="font-medium">{{ balance.currency.toUpperCase() }}</p>
                                <p class="text-sm text-muted-foreground">
                                    Last Updated: {{ formatDate(balance.updatedAtTs, undefined, true) }}
                                </p>
                            </div>
                            <div class="flex items-center gap-3 shrink-0">
                                <div class="text-right text-lg font-bold">
                                    {{ formatCurrency(balance.balance, balance.currency) }}
                                </div>
                                <Button variant="outline"
                                        size="sm"
                                        :disabled="balance.balance <= 0 || transferringCurrency === balance.currency"
                                        @click="requestTransfer(balance.currency)">
                                    <Loader2 v-if="transferringCurrency === balance.currency"
                                             class="h-4 w-4 animate-spin" />
                                    <BanknoteArrowDown v-else
                                                       class="h-4 w-4" />
                                    Request transfer
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-if="deliveryteamBalances && deliveryteamBalances.length > 0">
                    <h3 class="font-medium mb-2">Delivery teams</h3>
                    <div class="space-y-3">
                        <div v-for="balance in deliveryteamBalances"
                             :key="balance.currency"
                             class="flex items-center justify-between p-3 border rounded-lg gap-3">
                            <div class="min-w-0">
                                <p class="font-medium">{{ balance.currency.toUpperCase() }}</p>
                                <p class="text-sm text-muted-foreground">
                                    Last Updated: {{ formatDate(balance.updatedAtTs, undefined, true) }}
                                </p>
                            </div>
                            <div class="flex items-center gap-3 shrink-0">
                                <div class="text-right text-lg font-bold">
                                    {{ formatCurrency(balance.balance, balance.currency) }}
                                </div>
                                <Button variant="outline"
                                        size="sm"
                                        :disabled="balance.balance <= 0 || transferringDeliveryteamCurrency === balance.currency"
                                        @click="requestDeliveryteamTransfer(balance.currency)">
                                    <Loader2 v-if="transferringDeliveryteamCurrency === balance.currency"
                                             class="h-4 w-4 animate-spin" />
                                    <BanknoteArrowDown v-else
                                                       class="h-4 w-4" />
                                    Request transfer
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
